<template>
  <div
    class="ai-content-drawer"
    data-testid="product-card-drawer"
  >
    <div class="ai-content-drawer__header">
      <div class="ai-content-drawer__header-title">
        <AkHeading4>
          {{ t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.title') }}
        </AkHeading4>
        <AkBadge
          :color="AkBadgeColor.Blue"
          content="Beta"
          :size="AkBadgeSize.Xs"
          symbol="stars"
        />
      </div>
      <AkParagraph>
        {{ t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.description') }}
      </AkParagraph>
    </div>
    <div
      v-if="step === DrawerStep.Preparation"
      data-testid="preparation-step"
    >
      <div class="ai-content-drawer__form-row">
        <AkSelect
          v-model="campaignType"
          :label="t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.campaignType')"
          :options="campaignTypeOptions"
          class="ai-content-drawer__form-field"
          data-testid="campaign-type-select"
        />
      </div>
      <AkField
        :label="t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.products.label')"
        :label-extra="t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.optionalField')"
      >
        <ProductInlineSearch
          v-model:selected-products="selectedProducts"
          v-model:should-show-prices="shouldShowPrices"
          :image-cdn-service="imageCdnService"
          data-testid="product-inline-search"
        />
      </AkField>
      <div class="ai-content-drawer__form-row">
        <AkTextarea
          v-model="additionalInstructions"
          :rows="5"
          :has-error="errors.additionalInstructions"
          :errors="errors.additionalInstructions"
          :label="t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.additionalInstructions.label')"
          :label-extra="t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.optionalField')"
          :placeholder="t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.additionalInstructions.placeholder')"
          class="ai-content-drawer__form-field"
          data-testid="additional-instructions-textarea"
        />
      </div>
    </div>
    <div v-else>
      <AiContentLoader
        v-if="isLoadingResult"
        data-testid="ai-content-loader"
      />
      <div
        v-else
        data-testid="ai-content-result"
        v-html="visibleResult"
      />
    </div>
    <div class="ai-content-drawer__footer">
      <div class="ai-content-drawer__footer-inner">
        <AkButton
          size="lg"
          outlined
          data-testid="cancel-button"
          :disabled="isLoadingResult"
          @click="handleBackClick"
        >
          {{ step === DrawerStep.Preparation ? t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.cancel') : t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.back') }}
        </AkButton>
        <AkButton
          size="lg"
          data-testid="submit-button"
          :disabled="isLoadingResult"
          @click="handleNextClick"
        >
          {{ step === DrawerStep.Preparation ? t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.generateContent') : t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.insertContent') }}
        </AkButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AkHeading4, AkParagraph, AkButton, AkField, AkTextarea, AkSelect, AkBadge, AkBadgeColor, AkBadgeSize } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import { ProductInlineSearch } from '../shared/product-inline-search';
import { Amount } from '@client/monolith/core/types/amount';
import { ref, computed, reactive } from 'vue';
import { ImageCdnService } from '../types';
import AiContentLoader from './ai-content-loader.vue';
import { CrmTask } from '@client/monolith/legacy/types/ankoragent';
import { runCrmTask } from '@client/monolith/legacy/services/api/ankoragent';
import { useStore } from 'vuex';
import { getContent } from './templates';
import { campaignApplicableLanguages, liftCodeApplicableCountries } from './config';
import { getDiscountBrandEvent } from '@monolith/legacy/services/api/business-events';
import { onMounted } from 'vue';
import { isEnabled, FeatureFlag } from '@client/monolith/legacy/services/features';
import { CaptureErrorFunc } from '../../use-error-logger';

const AKS_DAYS_EVENT_ID = 186;

type SimpleProduct = {
  uuid: string;
  name: string;
  imgSrc?: string;
  price: Amount;
  link: string;
};

enum DrawerStep {
  Preparation = 'preparation',
  Result = 'result'
}

const { t } = useI18n();

const campaignTypeOptions = computed(() => {
  const options = [
    { label: t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.campaignType.options.newProducts'), value: CrmTask.NewProducts },
    { label: t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.campaignType.options.voucherFees'), value: CrmTask.VoucherFees },
    { label: t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.campaignType.options.activateRetailer'), value: CrmTask.ActivateRetailer },
    { label: t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.campaignType.options.reactivateRetailer'), value: CrmTask.ReactivateRetailer },
  ];

  if (isEnabled(FeatureFlag.AksDays)) {
    options.unshift({ label: t('account.brands.createCampaigns.editor.toolbar.insertAiContent.drawer.campaignType.options.aksDays'), value: CrmTask.AksDays });
  }

  return options;
});

const { locale } = useI18n();
const store = useStore();

const step = ref(DrawerStep.Preparation);
const isLoadingResult = ref(false);
const visibleResult = ref<string | null>(null);
const result = ref<Record<string, string> | null>(null);
const selectedProducts = ref<SimpleProduct[]>([]);
const shouldShowPrices = ref(true);
const campaignType = ref(campaignTypeOptions.value[0]?.value);
const additionalInstructions = ref('');
const aksDaysEventDiscountPercentage = ref<number | null>(null);
const errors = reactive({
  additionalInstructions: undefined,
});

const props = defineProps<{
  imageCdnService: ImageCdnService;
  captureError: CaptureErrorFunc;
}>();

defineOptions({
  name: 'AiContentDrawer'
});

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'submit', result: Record<string, string>): void;
}>();

const catalogueLink = computed(() => store.getters.brand?.link);

const handleBackClick = () => {
  if (step.value === DrawerStep.Preparation) {
    emit('close');
  } else {
    step.value = DrawerStep.Preparation
  }
}

onMounted(async () => {
  if (isEnabled(FeatureFlag.AksDays)) {
    const aksDaysEvent = await getDiscountBrandEvent(AKS_DAYS_EVENT_ID);
    aksDaysEventDiscountPercentage.value = aksDaysEvent.current?.discount;
  }
});

const handleNextClick = async () => {
  if (step.value === DrawerStep.Preparation) {
    try {
      isLoadingResult.value = true;
      step.value = DrawerStep.Result;

      const crmTasks = campaignApplicableLanguages.map((language) => runCrmTask({
        task: campaignType.value,
        additionalInstructions: additionalInstructions.value,
        targetCountries: liftCodeApplicableCountries,
        messagesURL: window.location.origin + '/messages',
        catalogueLink: window.location.origin + catalogueLink.value,
        language,
        freeDeliveryThreshold: '[add your Ankorstore free shipping threshold]',
        products: selectedProducts.value,
        discount: aksDaysEventDiscountPercentage.value,
      }));

      const [fr, de, en, es, it] = await Promise.all(crmTasks);

      const content = {
        fr: getContent(fr.output),
        de: getContent(de.output),
        en: getContent(en.output),
        es: getContent(es.output),
        it: getContent(it.output),
      };

      result.value = content;
      visibleResult.value = content[locale.value];
    } catch (e) {
      props.captureError('Error while running CRM generate email AI task', e, 'AiContentDrawer');
    } finally {
      isLoadingResult.value = false;
    }

  } else {
    emit('submit', result.value);
  }
};
</script>

<style lang="scss" scoped>
.ai-content-drawer {
  @apply ds-p-6;

  &__header {
      @apply ds-mb-4 ds-border-b ds-border-neutral-300 ds-border-solid ds-pb-4;
  }

  &__header-title {
    @apply ds-flex ds-items-center ds-gap-4;
  }

  &__footer {
    @apply ds-shadow-md ds-p-4 ds-absolute ds-bottom-0 ds-left-0 ds-w-full ds-bg-white ds-flex;
  }

  // Flip twice to allow box shadow to appear on top of footer
  &__footer-inner, &__footer {
    transform: rotate(180deg);
    @apply ds-flex ds-gap-4;
  }

  &__form-row {
    @apply ds-flex ds-gap-5 ds-my-6;
  }

  &__form-field {
    @apply ds-flex-1;
  }
}
</style>
